import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { CheckCircle, XCircle, Trash2, RefreshCw, Clock, User, Database, Play, X, AlertTriangle } from 'lucide-react';

export default function VoiceQueries() {
  const [queries, setQueries]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [working, setWorking]   = useState(null);
  const [testModal, setTestModal] = useState(null); // { query, result, error, loading }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/voice/queries');
      setQueries(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id, approved) => {
    setWorking(id);
    try {
      await apiClient.patch(`/voice/queries/${id}/approve`, { approved });
      setQueries(prev => prev.map(q => q.QueryID === id ? { ...q, IsApproved: approved ? 1 : 0 } : q));
    } finally { setWorking(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this query?')) return;
    setWorking(id);
    try {
      await apiClient.delete(`/voice/queries/${id}`);
      setQueries(prev => prev.filter(q => q.QueryID !== id));
    } finally { setWorking(null); }
  };

  const testQuery = async (q) => {
    setTestModal({ query: q, loading: true, result: null, error: null });
    try {
      const { data } = await apiClient.post(`/voice/queries/${q.QueryID}/test`);
      setTestModal(prev => ({ ...prev, loading: false, result: data }));
    } catch (e) {
      setTestModal(prev => ({ ...prev, loading: false, error: e.response?.data?.error || e.message }));
    }
  };

  const pending  = queries.filter(q => !q.IsApproved);
  const approved = queries.filter(q =>  q.IsApproved);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Voice Agent Queries</h1>
          <p className="text-sm text-gray-500 mt-1">
            Queries suggested by the AI agent during calls. Approve them to run automatically at the start of every future call.
          </p>
        </div>
        <button onClick={load} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* How it works */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 space-y-1">
        <p className="font-semibold">How it works</p>
        <p>When you ask the voice agent to "remember to always tell me X" or "add a query for...", it writes a SQL query and saves it here with <span className="font-medium">Pending</span> status.</p>
        <p>Use <span className="font-medium">Test</span> to preview results before approving. Once approved, the query runs at the start of every call and results are injected into the agent's context.</p>
        <p className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span>Always review the SQL before approving — reject queries with hardcoded GUIDs or specific IDs (they become stale). Good queries use JOINs and date functions like GETDATE().</span></p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : (
        <>
          {/* Pending */}
          <section>
            <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Pending Approval
              {pending.length > 0 && (
                <span className="ml-1 bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">{pending.length}</span>
              )}
            </h2>
            {pending.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-xl">No pending queries</p>
            ) : (
              <div className="space-y-3">
                {pending.map(q => (
                  <QueryCard key={q.QueryID} q={q} working={working}
                    onApprove={() => approve(q.QueryID, true)}
                    onRemove={() => remove(q.QueryID)}
                    onTest={() => testQuery(q)} />
                ))}
              </div>
            )}
          </section>

          {/* Approved */}
          <section>
            <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Active Queries
              {approved.length > 0 && (
                <span className="ml-1 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">{approved.length}</span>
              )}
            </h2>
            {approved.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center border border-dashed border-gray-200 rounded-xl">No approved queries yet</p>
            ) : (
              <div className="space-y-3">
                {approved.map(q => (
                  <QueryCard key={q.QueryID} q={q} working={working}
                    onRevoke={() => approve(q.QueryID, false)}
                    onRemove={() => remove(q.QueryID)}
                    onTest={() => testQuery(q)} />
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* Test Results Modal */}
      {testModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
              <div>
                <h3 className="font-semibold text-gray-900">Test Results</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate max-w-md">{testModal.query.Question}</p>
              </div>
              <button onClick={() => setTestModal(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-auto flex-1 p-5 space-y-4">
              {/* SQL */}
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">SQL (with TOP 50 guard)</p>
                <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">
                  {testModal.query.SqlTemplate}
                </pre>
              </div>

              {testModal.loading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
                  <RefreshCw className="h-4 w-4 animate-spin" />Running query…
                </div>
              )}

              {testModal.error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span><span className="font-semibold">Error: </span>{testModal.error}</span>
                </div>
              )}

              {testModal.result && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    {testModal.result.rows.length === 0 ? 'No rows returned' : `${testModal.result.rows.length} row${testModal.result.rows.length !== 1 ? 's' : ''} returned (max 50)`}
                  </p>
                  {testModal.result.rows.length > 0 && (
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            {testModal.result.columns.map(col => (
                              <th key={col} className="px-3 py-2 font-semibold text-gray-600 whitespace-nowrap">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {testModal.result.rows.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              {testModal.result.columns.map(col => (
                                <td key={col} className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-xs truncate">
                                  {row[col] === null ? <span className="text-gray-300">NULL</span> : String(row[col])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QueryCard({ q, working, onApprove, onRevoke, onRemove, onTest }) {
  const busy = working === q.QueryID;
  const date = (d) => d ? new Date(d).toLocaleDateString() : '—';

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${q.IsApproved ? 'border-green-200 bg-green-50/40' : 'border-amber-200 bg-amber-50/40'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{q.Question}</p>
          <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1"><User className="h-3 w-3" />{q.CreatedByName}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Created {date(q.CreatedAt)}</span>
            {q.LastRunAt && <span className="flex items-center gap-1"><RefreshCw className="h-3 w-3" />Last run {date(q.LastRunAt)} ({q.RunCount}×)</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={onTest} disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors disabled:opacity-50">
            <Play className="h-3.5 w-3.5" />Test
          </button>
          {!q.IsApproved && (
            <button onClick={onApprove} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50">
              <CheckCircle className="h-3.5 w-3.5" />Approve
            </button>
          )}
          {q.IsApproved && (
            <button onClick={onRevoke} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg transition-colors disabled:opacity-50">
              <XCircle className="h-3.5 w-3.5" />Revoke
            </button>
          )}
          <button onClick={onRemove} disabled={busy}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* SQL preview */}
      <details className="text-xs">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-700 flex items-center gap-1 select-none">
          <Database className="h-3 w-3" />View SQL
        </summary>
        <pre className="mt-2 bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap text-[11px] font-mono">
          {q.SqlTemplate}
        </pre>
      </details>
    </div>
  );
}
